<template>
    <Dialog
        v-model:visible="visibleVideo"
        :modal="true"
        :showHeader="false"
        :dismissableMask="true"
        contentClass="breadcrumbs_video__dialog_content overflow-hidden"
        :pt="{
            mask: {class: (isPiP ? 'hidden' : 'flex')}
        }"
    >
        <div class="relative">
            <video
                ref="videoPlayer"
                autoplay
                controls
                class="video-js vjs-default-skin w-full breadcrumbs_video__video_container"
                @enterpictureinpicture="onEnterPiP"
                @leavepictureinpicture="onLeavePiP"
                :disablePictureInPicture="!isAllowedPiP"
                controlsList="nodownload noplaybackrate"
                :pip="isAllowedPiP"
            ></video>
            <div class="breadcrumbs_video__social_container">
                <i
                    v-if="youtubeId"
                    class="pi pi-youtube breadcrumbs_video__youtube"
                    :title="$t('lang.main.see_youtube_title')"
                    @click="onOpenYoutube"
                ></i>
                <img
                    v-if="rutubeId"
                    class="breadcrumbs_video__rutube"
                    src="/i/icons/rutube.webp"
                    :title="$t('lang.main.see_rutube_title')"
                    @click="onOpenRutube"
                    alt=""
                />
                <i
                    class="pi pi-times breadcrumbs_video__close"
                    :title="$t('lang.main.video_close_popup_title')"
                    :style="getMarginStyle"
                    @click="onClose"
                ></i>
            </div>
        </div>
    </Dialog>
</template>

<script>
import videojs from "video.js";
import "video.js/dist/video-js.css";
import {nextTick} from "vue";
import Dialog from "primevue/dialog";
import {Listeners} from "keysso/constants/listeners";
import Alerts from "keysso/mixins/Alerts";

const SELECTEL_URL_TEMPLATE = 'https://keys-so-video.keys.so/video/instruction/%s_%s.mp4';
const YOUTUBE_URL_TEMPLATE = 'https://www.youtube.com/watch?v=%s';
const RUTUBE_URL_TEMPLATE = 'https://rutube.ru/video/%s/';

export default {
    name: "BreadcrumbsVideo",
    emits: [Alerts],
    props: {
        /**
         * "ID" видео файла (имя без разрешения и расширения. Например, файл compare_1080p.mp4 - передаем compare.
         * Важно!!!
         * 1. Все видео должны иметь 3 разрешения: 480p, 720p, 1080p.
         * 2. Должны быть в формате .mp4, контейнер в свойствах изображения не должен быть MPEG-2 TS
         * 3. В s3 должны именть имена в форматах <name>_480p.mp4, <name>_720p.mp4, <name>_1080p.mp4.
         * Все подробности в README_FRONT.md
         */
        videoName: {
            type: String,
            required: true,
        },
        youtubeId: {
            type: String,
            required: false,
        },
        rutubeId: {
            type: String,
            required: false,
        }
    },
    data() {
        return {
            visibleVideo: false,
            currentVideo: null,
            isFullscreen: false,
            isPiP: false,
            isAllowedPiP: false,
            player: null, // Ссылка на плеер Video.js
        };
    },
    mounted() {
        this.$emitter.on(Listeners.BLOCK_BREADCRUMBS_VIDEO_OPEN, name => {
            this.visibleVideo = true;
            nextTick(() => {
                this.checkBrowser();
                this.initializePlayer();
            })
        });

        document.addEventListener("fullscreenchange", this.onToggleFullscreenUI);
        document.addEventListener("webkitfullscreenchange", this.onToggleFullscreenUI);
        document.addEventListener("mozfullscreenchange", this.onToggleFullscreenUI);
        document.addEventListener("MSFullscreenChange", this.onToggleFullscreenUI);
    },
    beforeUnmount() {
        document.removeEventListener("fullscreenchange", this.onToggleFullscreenUI);
        document.removeEventListener("webkitfullscreenchange", this.onToggleFullscreenUI);
        document.removeEventListener("mozfullscreenchange", this.onToggleFullscreenUI);
        document.removeEventListener("MSFullscreenChange", this.onToggleFullscreenUI);
    },
    beforeDestroy() {
        if (this.player) {
            this.player.dispose();
        }
    },
    computed: {
        getMarginStyle() {
            return (this.youtubeId || this.rutubeId) ? 'margin-left: 10px' : null;
        },
    },
    methods: {
        initializePlayer() {
            const videoElement = this.$refs.videoPlayer;

            if (videoElement) {
                videojs.addLanguage('ru', {
                    "Play": "Воспроизвести",
                    "Pause": "Пауза",
                    "Mute": "Выключить звук",
                    "Unmute": "Включить звук",
                    "Fullscreen": "Полный экран",
                    "Exit Fullscreen": "Выйти из полного экрана",
                    "Picture-in-Picture": "Картинка в картинке",
                    "Exit Picture-in-Picture": "Закрыть картинку в картинке",
                    "Replay": "Повторить",
                    "Current Time": "Текущее время",
                    "Duration": "Длительность",
                    "Remaining Time": "Оставшееся время",
                    "Playback Rate": "Скорость воспроизведения",
                    "Subtitles": "Субтитры",
                    "Captions": "Заголовки",
                    "Chapters": "Главы",
                    "Settings": "Настройки",
                    "Loaded": "Загружено",
                    "Live Broadcast": "Прямой эфир",
                    "Volume Level": "Уровень громкости",
                });
                // Инициализация плеера Video.js
                this.player = videojs(videoElement, {
                    controls: true,
                    preload: "auto",
                    controlBar: {
                        pictureInPictureToggle: this.isAllowedPiP, // Включаем кнопку PiP
                    },
                    language: 'ru'
                });

                this.player.src({
                    src: this.getQualityUrl('480p'),
                    type: 'video/mp4'
                });

                // Обработка события включения PiP
                this.player.on("enterpictureinpicture", () => {
                    this.onEnterPiP();
                });

                this.player.on("leavepictureinpicture", () => {
                    this.onLeavePiP();
                });
            } else {
                this.alertError('Не удалось найти видео-элемент.')
            }
        },
        checkBrowser() {
            const userAgent = navigator.userAgent;
            const vendor = navigator.vendor;

            this.isAllowedPiP = (
                (/Chrome/.test(userAgent) && !/YaBrowser/.test(userAgent)) // Chrome
                || (/Safari/.test(userAgent) && /Apple/.test(vendor)) // Safari
                || /Edg/.test(userAgent) // Edge
            );
        },
        onToggleFullscreenUI() {
            const isFullscreen =
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement;

            this.isFullscreen = !!isFullscreen;

            if (isFullscreen) {
                this.setQuality(window.innerWidth >= 1200 ? '1080p' : '720p');
            } else {
                this.setQuality('480p');
            }
        },
        setQuality(quality) {
            const src = this.getQualityUrl(quality);

            if (!this.player) {
                this.currentVideo = src;
                return;
            }

            const time = this.player.currentTime();
            const paused = this.player.paused();

            this.player.src({
                src,
                type: 'video/mp4'
            });

            this.player.ready(() => {
                this.player.currentTime(time);
                if (!paused) {
                    this.player.play();
                }
            });
        },
        getQualityUrl(quality) {
            return sprintf(SELECTEL_URL_TEMPLATE, this.videoName, quality);
        },
        onOpenYoutube() {
            window.open(sprintf(YOUTUBE_URL_TEMPLATE, this.youtubeId));
        },
        onOpenRutube() {
            window.open(sprintf(RUTUBE_URL_TEMPLATE, this.rutubeId));
        },
        onClose() {
            this.visibleVideo = false;
        },
        onEnterPiP() {
            this.isPiP = true;
            document.body.classList.remove("p-overflow-hidden");
        },
        onLeavePiP() {
            this.isPiP = false;
            document.body.classList.add("p-overflow-hidden");
        },
    },
    components: {
        Dialog
    },
}
</script>

<style>
.breadcrumbs_video__dialog_content {
    padding: 0 !important;
    width: 854px;
    max-width: 100vw;
    border-radius: 6px;
    line-height: 0;
}
</style>
<style scoped>
.breadcrumbs_video__video_container {
    height: auto;
    aspect-ratio: 16 / 9;
    margin: -1px 0;
}
.breadcrumbs_video__social_container {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px;
    background-color: #37415154;
    border-radius: 6px;
    opacity: .7;
    margin: 8px;
    transition: opacity .3s;
    z-index: 0;
}
.breadcrumbs_video__social_container:hover {
    opacity: 1;
}
.breadcrumbs_video__rutube {
    height: 24px !important;
    border-radius: 6px;
    cursor: pointer;
}
.breadcrumbs_video__youtube {
    font-size: 24px;
    color: red;
    cursor: pointer;
    position: relative;
}
.breadcrumbs_video__youtube:after {
    content: '';
    width: 12px;
    height: 12px;
    background: #ffffffa3;
    top: 6px;
    right: 6px;
    position: absolute;
    z-index: -1;
}
.breadcrumbs_video__close {
    font-size: 16px;
    color: #fff;
    cursor: pointer;
    margin-left: v-bind(marginSocial);
}
.breadcrumbs_video__close:before {
    text-shadow: 0 0 3px #000;
}
</style>
